import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { QrCode, Info, Smartphone, Trash2, Rocket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { RemoveMobileDialog } from './RemoveMobileDialog'
import { PublishModal } from '@/components/mobile/PublishModal'

interface MobilePreviewSidePanelProps {
  onOpenQRDialog: () => void
  onRemoveMobile?: () => Promise<void>
  isProcessing?: boolean
  compact?: boolean
  projectId?: number
  projectSlug?: string
}

export function MobilePreviewSidePanel({
  onOpenQRDialog,
  onRemoveMobile,
  isProcessing = false,
  compact = false,
  projectId,
  projectSlug,
}: MobilePreviewSidePanelProps) {
  const { t } = useTranslation('conversations')
  const { props } = usePage<{ auth: { user: { super_admin?: boolean } } }>()
  const isSuperAdmin = props.auth?.user?.super_admin === true
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)

  const handleRemoveMobile = async () => {
    if (onRemoveMobile) {
      await onRemoveMobile()
    }
  }

  if (compact) {
    return (
      <div className="flex flex-col gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
            >
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="left" className="max-w-[220px] text-xs text-amber-800 dark:text-amber-200 mt-18">
            {t('preview.mobileFrame.disclaimer')}
          </PopoverContent>
        </Popover>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              onClick={onOpenQRDialog}
            >
              <QrCode className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{t('preview.mobileFrame.testOnDevice')}</TooltipContent>
        </Tooltip>

        {isSuperAdmin && projectId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                onClick={() => setShowPublishModal(true)}
              >
                <Rocket className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{t('sidebar.publishToTestFlight', { ns: 'mobile' })}</TooltipContent>
          </Tooltip>
        )}

        {onRemoveMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowRemoveDialog(true)}
                disabled={isProcessing}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{t('preview.removeMobile.sectionTitle')}</TooltipContent>
          </Tooltip>
        )}

        <RemoveMobileDialog
          open={showRemoveDialog}
          onOpenChange={setShowRemoveDialog}
          onConfirm={handleRemoveMobile}
        />
        {isSuperAdmin && projectId && projectSlug && (
          <PublishModal
            open={showPublishModal}
            onOpenChange={setShowPublishModal}
            projectId={projectId}
            projectSlug={projectSlug}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex-shrink-0 max-w-[280px] pt-6 space-y-4">
      {/* Disclaimer */}
      <div className="flex gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
        <Info className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
          {t('preview.mobileFrame.disclaimer')}
        </p>
      </div>

      {/* QR Code section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Smartphone className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('preview.mobileFrame.testOnDevice')}
          </span>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={onOpenQRDialog}
        >
          <QrCode className="h-4 w-4" />
          {t('preview.mobileFrame.scanToTest')}
        </Button>
      </div>

      {/* Publish to TestFlight */}
      {isSuperAdmin && projectId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('sidebar.publishToTestFlight', { ns: 'mobile' })}
            </span>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowPublishModal(true)}
          >
            <Rocket className="h-4 w-4" />
            {t('sidebar.publish', { ns: 'mobile' })}
          </Button>
        </div>
      )}

      {/* Remove Mobile section */}
      {onRemoveMobile && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('preview.removeMobile.sectionTitle')}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {t('preview.removeMobile.sectionDescription')}
          </p>
          <Button
            variant="outline"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setShowRemoveDialog(true)}
            disabled={isProcessing}
          >
            <Trash2 className="h-4 w-4" />
            {t('preview.removeMobile.button')}
          </Button>
        </div>
      )}

      <RemoveMobileDialog
        open={showRemoveDialog}
        onOpenChange={setShowRemoveDialog}
        onConfirm={handleRemoveMobile}
      />
      {isSuperAdmin && projectId && projectSlug && (
        <PublishModal
          open={showPublishModal}
          onOpenChange={setShowPublishModal}
          projectId={projectId}
          projectSlug={projectSlug}
        />
      )}
    </div>
  )
}
